// A PARENT TASK IS A GROUP, NOT A TASK. The moment a task has a subtask it
// stops being a piece of work and becomes the heading its subtasks sit under:
// no checkbox, no status, no due date, no assignees of its own on any screen,
// not counted as an open task, not a card on the Kanban, not a bar on the
// Gantt. The work is the leaves. Both clients render off these rules so a
// parent cannot read as a group on one screen and as a task on another.
//
// The API says how many live children a task has (`childCount`, completed
// ones included) and how many of those are still open (`openChildCount`); the
// two counts are what every rule here is derived from.

export interface TaskTreeNode {
  childCount?: number | null;
  openChildCount?: number | null;
  isCompleted?: boolean | number | null;
  parentTaskID?: string | null;
}

/** A task with at least one live subtask renders as a group header. */
export function isTaskGroup(t: TaskTreeNode | null | undefined): boolean {
  return Number(t?.childCount ?? 0) > 0;
}

/** Open subtasks under a group. Read off the API count; an absent count (an
 *  older API) falls back to the total so a group never advertises fewer
 *  subtasks than it has. */
export function openSubtaskCount(t: TaskTreeNode | null | undefined): number {
  if (!t) return 0;
  const open = t.openChildCount;
  if (open === null || open === undefined) return Number(t.childCount ?? 0);
  return Number(open);
}

/** A group has no completion of its own — it is done when its subtasks are.
 *  A leaf is done when its own flag says so. */
export function isEffectivelyComplete(t: TaskTreeNode | null | undefined): boolean {
  if (!t) return false;
  if (isTaskGroup(t)) return openSubtaskCount(t) === 0;
  return !!t.isCompleted;
}

/** The tasks that are work: every task that is not a group. Flat surfaces
 *  (Kanban, Gantt, the dashboard lists) show only these. */
export function leafTasks<T extends TaskTreeNode>(list: readonly T[] | null | undefined): T[] {
  return (list ?? []).filter((t) => !isTaskGroup(t));
}

/** The line a collapsed group shows under its title. */
export function subtaskHint(t: TaskTreeNode | null | undefined): string {
  const total = Number(t?.childCount ?? 0);
  if (total <= 0) return '';
  const open = openSubtaskCount(t);
  if (open === 0) return `All ${total} subtask${total === 1 ? '' : 's'} complete`;
  if (open === total) return `${open} subtask${open === 1 ? '' : 's'}`;
  return `${open} of ${total} subtasks open`;
}
